import { type FC, useMemo } from "react";

import type { StoreLocatorFilterCategory, StoreLocatorFilterItem } from "@locators/components/shared/types";

import { StoreLocatorFilterCategoryItem } from "../StoreLocatorFilterCategoryItem";

import styles from "./StoreLocatorFilterProductFlavoursCategory.module.scss";

type StoreLocatorFilterProductFlavoursCategoryProps = {
  filterCategory: StoreLocatorFilterCategory;
  isProductFlavoursLYOFilterEnabled?: boolean;
  changeFilters?: (category: string, filter: StoreLocatorFilterItem) => void;
  checkFiltersStatus?: (category: string, filerKey: string) => boolean;
};

export const StoreLocatorFilterProductFlavoursCategory: FC<StoreLocatorFilterProductFlavoursCategoryProps> = ({
  filterCategory,
  isProductFlavoursLYOFilterEnabled,
  changeFilters,
  checkFiltersStatus,
}) => {
  const filtersByType = useMemo(
    () =>
      filterCategory.filters.reduce(
        (acc, filter) => {
          if (filter.isLYO) {
            acc.lyo.push(filter);
          } else {
            acc.evo.push(filter);
          }
          return acc;
        },
        { evo: [], lyo: [] } as Record<"evo" | "lyo", StoreLocatorFilterItem[]>
      ),
    [filterCategory.filters]
  );

  const renderFilterItemsList = (filters: StoreLocatorFilterItem[]) =>
    filters.map((filter) => (
      <StoreLocatorFilterCategoryItem
        filter={filter}
        onChange={() => changeFilters?.(filterCategory.key, filter)}
        isChecked={checkFiltersStatus?.(filterCategory.key, filter.key) ?? false}
        filterCategory={filterCategory.key}
        key={filter.key}
      />
    ));

  const renderGroupedFilterCategory = (type: keyof typeof filtersByType, label: string) => {
    const filters = filtersByType[type];
    return filters.length > 0 ? (
      <div key={type} className={styles.productFlavoursCategoryContainer}>
        <h3 className={styles.productFlavoursCategoryTitle}>{label}</h3>
        <div className={styles.productFlavoursCategoryItems}>{renderFilterItemsList(filters)}</div>
      </div>
    ) : null;
  };

  if (!isProductFlavoursLYOFilterEnabled) {
    return <>{renderFilterItemsList(filterCategory.filters)}</>;
  }

  return (
    <div className={styles.productFlavoursContainer} data-testid="productFlavoursFilterCategoryContainer">
      {renderGroupedFilterCategory("evo", "EVO")}
      {renderGroupedFilterCategory("lyo", "LYO")}
    </div>
  );
};
